import { faStar as star } from '@fortawesome/free-regular-svg-icons';
import { faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { Box, Collapse, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { userInstrumentsSlice } from '!core/redux';
import {
  TrlFlag,
  TrlLabelValue,
  TrlPriceType,
  TrlTextPill,
} from '»components/atoms';
import { ButtonMinus, ButtonPlus } from '»components/atoms/Buttons';
import {
  useCurrentPage,
  useDocuments,
  useFundsTab,
  useIsTitleNegotiable,
  useNegModalContext,
  useTitleDetails,
  useTrancheTypeLabel,
} from '»utils/customHooks';
import { TrlDateFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { TrlFormattedMessagePlain, TrlFormattedNumberPlain } from '»utils/i18n';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';

import {
  DataDiv,
  DataLabelWallet,
  DataValueWallet,
  FavoriteIcon,
  FlagContainer,
  HeaderDiv,
  InfoDiv,
  LineSeparator,
  QuotationStyled,
  Separator,
  StockInfo,
  StockName,
  StockType,
  Wrapper,
  WrapperButtons,
  WrapperData,
  WrapperDataInfoWallet,
  WrapperDataLeftSideWallet,
  WrapperDataWallet,
  WrapperDataWalletRight,
  WrapperDetail,
  WrapperDetailWallet,
  WrapperHeaderName,
  WrapperStockData,
} from './TrlCard.styles';

export const TrlCard = ({ isWallet, data, setSelectedItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const isBelow700 = useMediaQuery(theme.breakpoints.down('md'));

  const titleDetails = useTitleDetails(data);
  const { documents } = useDocuments(
    titleDetails?.details[titleDetails.details.length - 1].noteId,
  );
  const { currentFundsTab } = useFundsTab();
  const { isMarketPrimaryPage, isFundsListPage } = useCurrentPage();
  const { getTrancheTypeLabel } = useTrancheTypeLabel();

  const isFavourite = data.isFavourite === 'Y';
  const isDisabledSetFavourite = !data.instrument;
  const PURCHASE = 'purchase';
  const SALE = 'sale';
  const isFundsPrimary = currentFundsTab === 'primary';

  const { openInsertOrderModal } = useNegModalContext();

  const isWalletTitleNegotiable = useIsTitleNegotiable({
    isMarketPrimaryScreen: isMarketPrimaryPage,
    isFundsPrimaryScreen: isFundsPrimary,
    canTrade: data?.canTrade,
  });

  const isPrimaryTitleNegotiable = useIsTitleNegotiable({
    isMarketPrimaryScreen: isMarketPrimaryPage,
    isFundsPrimaryScreen: isFundsPrimary,
    canTrade: data.canTrade,
    endDate: data.endDate,
    startDate: data.startDate,
  });

  const handleFavorite = (e) => {
    e.stopPropagation();
    dispatch(
      userInstrumentsSlice.actions.setFavInstrumentRequest({
        option: isFavourite ? 'D' : 'I',
        instrument: data.instrument,
        symbol: data.symbol,
        exchange: data.exchange,
        securityType: data.securityType,
      }),
    );
  };

  const openNegotiation = (operationType) => {
    if (setSelectedItem) setSelectedItem(data);

    if (isMarketPrimaryPage) {
      navigate('/Insert/Order/Primary', {
        state: { dataItem: data },
      });
    } else if (isFundsListPage) {
      navigate('/Market/Funds/NewOrder', {
        state: { dataItem: data },
      });
    } else {
      openInsertOrderModal(operationType === PURCHASE ? 1 : 2);
    }
  };

  const endDecorator = getPercentageDecorator(data.priceRepresentation);

  const { variationPctg } = data;

  const decimalDigits = getDecimalDigits(data.roundPrice);

  const getDetail = () => {
    if (isWallet)
      return (
        <WrapperDetailWallet id={titleDetails.detailsContainerId}>
          {titleDetails.details.map((detail) => (
            <TrlLabelValue
              key={detail.label}
              endDecorator={detail.endDecorator}
              values={detail.values}
              label={detail.label}
              isDetail
              isWallet
              decimalDigits={detail.decimalDigits}
              type={detail.type}
              style={detail.style}
            />
          ))}
        </WrapperDetailWallet>
      );

    return (
      <WrapperDetail id={titleDetails.detailsContainerId}>
        {titleDetails.details.map((detail) => (
          <TrlLabelValue
            key={detail.label}
            endDecorator={detail.endDecorator}
            values={detail.values}
            label={detail.label}
            isDetail
            isDocument={detail.isDocument}
            documents={detail.isDocument && documents}
            decimalDigits={detail.decimalDigits}
            type={detail.type}
            style={detail.style}
          />
        ))}
      </WrapperDetail>
    );
  };

  const getInfo = () => {
    if (isWallet) {
      return (
        <>
          <StockInfo>
            <Box>{data.symbol}</Box>
            <Separator />
            <FlagContainer>
              <TrlFlag flag={data.country} small={isBelow700} />
              {data.exchangeDescr}
            </FlagContainer>
            <Separator />
            <Box>{data.currency}</Box>
          </StockInfo>

          <StockType isWallet>
            <FormattedNumber
              value={data.currentValue}
              maximumFractionDigits={decimalDigits}
              minimumFractionDigits={decimalDigits}
            />
          </StockType>
        </>
      );
    }

    if (isFundsPrimary)
      return (
        <StockInfo>
          {TrlFormattedNumberPlain(data.subscriptionPrice, 2)} {data.currency}
        </StockInfo>
      );

    const trancheTypeLabel = getTrancheTypeLabel(data.trancheType);

    return (
      <>
        <StockInfo>
          {data.currency}
          <Separator />
          {trancheTypeLabel}
          <Separator />
          <TrlPriceType value={data.priceType} />
        </StockInfo>
        <StockType>{data.offerType}</StockType>
      </>
    );
  };

  const getData = () => {
    if (isWallet)
      return (
        <WrapperDataWallet id="divStockDataPortfolio">
          <WrapperDataLeftSideWallet>
            <WrapperDataInfoWallet isVariation>
              <DataLabelWallet>
                {TrlFormattedMessagePlain({ id: 'TRLT_UI-QUOTE' })}
              </DataLabelWallet>
              <QuotationStyled>
                <TrlNumberFormatted
                  value={data.lastPx}
                  fontFamily="Roboto"
                  endDecorator={endDecorator}
                  decimalDigits={decimalDigits}
                />
              </QuotationStyled>
            </WrapperDataInfoWallet>
            <WrapperDataInfoWallet isVariation>
              <DataLabelWallet>
                {TrlFormattedMessagePlain({ id: 'TRLT_UI-VARIATION' })}
              </DataLabelWallet>
              <QuotationStyled variationPctg={variationPctg}>
                <FormattedNumber
                  value={variationPctg}
                  maximumFractionDigits={decimalDigits}
                  minimumFractionDigits={decimalDigits}
                />
              </QuotationStyled>{' '}
            </WrapperDataInfoWallet>
          </WrapperDataLeftSideWallet>
          <WrapperDataWalletRight>
            <WrapperDataInfoWallet>
              <DataLabelWallet>
                {TrlFormattedMessagePlain({ id: 'TRLT_UI-POTENCIAL_GAINS' })}
              </DataLabelWallet>
              <DataValueWallet>
                <FormattedNumber
                  value={data.potencialGainsBase}
                  maximumFractionDigits={decimalDigits}
                  minimumFractionDigits={decimalDigits}
                />
              </DataValueWallet>
            </WrapperDataInfoWallet>
            <WrapperDataInfoWallet>
              <DataLabelWallet>
                {TrlFormattedMessagePlain({
                  id: matches
                    ? 'TRLT_UI-TOTAL_QTY_LONG'
                    : 'TRLT_UI-TOTAL_QTY_SHORT',
                })}
              </DataLabelWallet>
              <DataValueWallet>{data.totalQuantity}</DataValueWallet>
            </WrapperDataInfoWallet>
          </WrapperDataWalletRight>
        </WrapperDataWallet>
      );

    return (
      <WrapperData id={titleDetails.datesContainerId}>
        {titleDetails.dates?.map((datesContainer) => (
          <WrapperStockData
            key={datesContainer[0].label + datesContainer[1].label}
          >
            {datesContainer.map((date) => (
              <TrlLabelValue
                key={date.label}
                label={TrlFormattedMessagePlain({ id: date.label })}
                values={
                  <>
                    <FormattedDate value={date.date} />
                    {date.hasTime && (
                      <>
                        {' '}
                        | <TrlDateFormatted date={date.date} option="time" />
                      </>
                    )}
                  </>
                }
              />
            ))}
          </WrapperStockData>
        ))}
      </WrapperData>
    );
  };

  return (
    <Wrapper
      isWallet={isWallet}
      isFundsPrimary={isFundsPrimary}
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
    >
      <HeaderDiv>
        <WrapperHeaderName isMarketPrimary>
          {isWallet ? (
            <>
              <FavoriteIcon
                disabled={isDisabledSetFavourite}
                icon={isFavourite ? solidStar : star}
                onClick={(e) => {
                  if (!isDisabledSetFavourite) handleFavorite(e);
                }}
              />
              <StockName>{data.instrumentDescr}</StockName>
            </>
          ) : (
            <StockName>
              {isFundsPrimary
                ? data.securityDescription
                : `${data.publicOfferId} | ${data.trancheDescr}`}
            </StockName>
          )}
        </WrapperHeaderName>
        {!isWallet && <TrlTextPill value={data.state} />}
      </HeaderDiv>
      <InfoDiv>
        {getInfo()}
        {matches && isWallet && (
          <WrapperButtons isWallet={isWallet}>
            <ButtonMinus
              disabled={!isWalletTitleNegotiable}
              isCard
              onClick={() => {
                openNegotiation(SALE);
              }}
            />
            <ButtonPlus
              disabled={!isWalletTitleNegotiable}
              isCard
              onClick={() => {
                openNegotiation(PURCHASE);
              }}
            />
          </WrapperButtons>
        )}
      </InfoDiv>

      <DataDiv isWallet={isWallet}>
        {getData()}
        {(!isWallet || (isWallet && !matches)) && (
          <WrapperButtons>
            {isWallet && (
              <ButtonMinus
                disabled={!isWalletTitleNegotiable}
                isCard
                onClick={() => {
                  openNegotiation(SALE);
                }}
              />
            )}
            <ButtonPlus
              disabled={!isPrimaryTitleNegotiable}
              isCard
              onClick={() => {
                openNegotiation(PURCHASE);
              }}
            />
          </WrapperButtons>
        )}
      </DataDiv>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <LineSeparator />
        {getDetail()}
      </Collapse>
    </Wrapper>
  );
};

TrlCard.propTypes = {
  isWallet: PropTypes.bool,
  data: PropTypes.object,
  setSelectedItem: PropTypes.func,
};
